import { useEffect, useRef } from 'react';
import { useAuthStore } from '@/stores/useAuthStore';
import { useAppStore } from '@/stores/useAppStore';
import { badgesService } from '@/services/badgesService';
import { supabase } from '@/integrations/supabase/client';

/**
 * ✅ FASE 1B: Hook otimizado para verificar badges
 * Executa apenas a cada 5 minutos ao invés de toda navegação
 * Reduz ~90% das verificações desnecessárias
 */
export const useBadgeCheckerOptimized = () => {
  const { user } = useAuthStore();
  const { goals, habits, challenge } = useAppStore();
  const lastCheck = useRef<Date | null>(null);

  useEffect(() => {
    if (!user) return;

    const checkBadges = async () => {
      const now = new Date();
      const fiveMinutes = 5 * 60 * 1000;
      
      // ✅ Só verificar a cada 5 minutos
      if (lastCheck.current && (now.getTime() - lastCheck.current.getTime()) < fiveMinutes) {
        console.log('⏭️ Badge check skipped (dentro do intervalo de 5min)');
        return;
      }
      
      lastCheck.current = now;
      console.log('🔍 Executando verificação otimizada de badges...');

      try {
        // Verificar badges de onboarding
        const { data: profile } = await supabase
          .from('profiles')
          .select('onboarding_completed')
          .eq('id', user.id)
          .single();

        if (profile?.onboarding_completed) {
          await badgesService.awardBadge(user.id, 'badge-onboarding');
          await badgesService.awardBadge(user.id, 'badge-wheel');
        }

        const maxStreak = challenge?.streak || 0;
        
        // Badge de streak
        if (maxStreak >= 7) {
          await badgesService.awardBadge(user.id, 'badge-streak7', { streak: maxStreak });
        }
        if (maxStreak >= 21) {
          await badgesService.awardBadge(user.id, 'badge-streak21', { streak: maxStreak });
        }
        if (maxStreak >= 66) {
          await badgesService.awardBadge(user.id, 'badge-streak66', { streak: maxStreak });
        }
        if (maxStreak >= 90) {
          await badgesService.awardBadge(user.id, 'badge-streak90', { streak: maxStreak });
        }

        // Badge de primeira meta
        if (goals.length > 0 && goals.some(g => g.userId === user.id)) {
          const firstGoal = goals.find(g => g.userId === user.id);
          if (firstGoal) {
            await badgesService.awardBadge(user.id, 'badge-goal1', { 
              goalTitle: firstGoal.title 
            });
          }
        }

        // Badge de primeiro hábito
        if (habits.length > 0 && habits.some(h => h.userId === user.id)) {
          const firstHabit = habits.find(h => h.userId === user.id);
          if (firstHabit) {
            await badgesService.awardBadge(user.id, 'badge-habit1', { 
              habitTitle: firstHabit.title 
            });
          }
        }
      } catch (error) {
        console.error('❌ Erro ao verificar badges:', error);
      }
    };

    checkBadges();
  }, [user, goals.length, habits.length, challenge?.streak]);
};
